package horcrux.common.api.dtos

import arrow.core.Either
import arrow.core.raise.either
import com.benasher44.uuid.Uuid
import com.benasher44.uuid.uuid4
import com.benasher44.uuid.uuidFrom
import horcrux.common.HasBetterToString
import horcrux.common.HorcruxRequestSimpleError
import horcrux.common.UuidSerializer
import horcrux.common.ValidatedValueClass
import horcrux.common.api.dtos.types.CollectionID
import horcrux.common.api.dtos.types.CollectionMetadata
import horcrux.common.api.dtos.types.CollectionName
import horcrux.common.api.dtos.types.ContextDocumentChunkAndMetadata
import horcrux.common.api.dtos.types.HasID
import horcrux.common.api.dtos.types.ParameterName
import horcrux.common.api.dtos.types.UserID
import horcrux.common.configs.DocumentStorageConfigs
import horcrux.common.types.TruncatedString
import io.ktor.http.HttpStatusCode
import kotlinx.datetime.Instant
import kotlinx.serialization.Serializable
import kotlin.jvm.JvmInline

@Serializable
sealed interface ChatMessage {
    val message: TruncatedString

    companion object
}

@Serializable
data class UserChatMessage(
    override val message: TruncatedString,
) : ChatMessage

@Serializable
data class TheGrokAppChatMessage(
    override val message: TruncatedString,
    val contextDocumentChunkAndMetadataList: List<ContextDocumentChunkAndMetadata>
) : ChatMessage

interface CloudStorageIdentifier : HasBetterToString {
    val cloudStorageBasePath: String
    val cloudStorageKey: String
        get() = "$cloudStorageBasePath/${betterToString()}"
}

@JvmInline
@Serializable
value class ChatPageID(@Serializable(with = UuidSerializer::class) override val value: Uuid) :
    ValidatedValueClass<Uuid, ChatPageID>, CloudStorageIdentifier {
    constructor() : this(uuid4())

    override val cloudStorageBasePath: String
        get() = "${DocumentStorageConfigs.CHAT_PAGE_DATA_BASE_PATH}"

    companion object {
        val PARAMETER_NAME: ParameterName = ParameterName("chatPageID")

        operator fun invoke(value: String): Either<Throwable, ChatPageID> = Either.catch {
            ChatPageID(uuidFrom(value))
        }
    }
}

@Serializable
data class PostChatPageRequest(
    val collectionID: CollectionID,
    val chatMessages: List<ChatMessage>,
    val collectionNameMaybe: CollectionName?,
) : HorcruxValidatedRequest

@Serializable
data class ChatPageMetadata(
    override val id: ChatPageID,
    val collectionMetadata: CollectionMetadata,
    val createdAt: Instant,
) : HasID<ChatPageID>

@Serializable
data class PostChatPageResponse(
    val chatPageMetadata: ChatPageMetadata
) : HorcruxResponse

open class PostChatPageError(httpStatusCode: HttpStatusCode) : HorcruxRequestSimpleError(httpStatusCode)

data class ErrorWritingChatMessagesToCloudStorage(
    val chatPageID: ChatPageID, val collectionID: CollectionID, val userID: UserID, override val cause: Throwable?
) : PostChatPageError(HttpStatusCode.BadGateway)

data class ErrorCreatingChatPageDatabaseEntry(
    val chatPageID: ChatPageID, val collectionID: CollectionID, val userID: UserID, override val cause: Throwable?
) : PostChatPageError(HttpStatusCode.BadGateway)

@Serializable
data object GetChatPageResponse : HorcruxResponse

open class GetChatPageError(httpStatusCode: HttpStatusCode) : HorcruxRequestSimpleError(httpStatusCode)

data class ErrorGettingChatPageFromDatabase(
    val chatPageID: ChatPageID, val userID: UserID, override val cause: Throwable?
) : GetChatPageError(HttpStatusCode.BadGateway)

data class GetChatPageMalformedRequest(
    val parameterName: String,
    val userID: UserID,
) : GetChatPageError(HttpStatusCode.BadRequest) {
    override val message: String = "Parameter $parameterName was missing from request"
}

data class ErrorGettingChatMessagesFromCloudStorage(
    val chatPageID: ChatPageID, val userID: UserID, override val cause: Throwable?
) : GetChatPageError(HttpStatusCode.BadGateway)

data class ErrorStartingChatPageGeneration(override val cause: Throwable) :
    GetChatPageError(HttpStatusCode.InternalServerError)

data class ErrorGeneratingChatPage(override val message: String?) : GetChatPageError(HttpStatusCode.InternalServerError)

data class ErrorRetrievingChildDocuments(
    val collectionID: CollectionID, val userID: UserID, override val cause: Throwable?
) : GetChatPageError(
    HttpStatusCode.BadGateway
)

@Serializable
data class DeleteChatPageRequest(
    val chatPageID: ChatPageID
) : HorcruxValidatedRequest

@Serializable
data object DeleteChatPageResponse : HorcruxResponse

open class DeleteChatPageError(httpStatusCode: HttpStatusCode) : HorcruxRequestSimpleError(httpStatusCode)

data class UserDoesNotOwnChatPage(val chatPageID: ChatPageID, val userID: UserID) :
    DeleteChatPageError(HttpStatusCode.Unauthorized)
